import React, { useState } from 'react'
import { Upload, message, Button, Spin, Popover } from 'antd'
import type { UploadProps } from 'antd'
import { ImagePlus, Maximize2 } from 'lucide-react'
import type { LineItem } from '../../types/ffe'
import { useApi } from '../../services/ApiContext'
import { useFFEStore } from '../../store/ffeStore'

interface Props {
  item: LineItem
  onUpdate: (updates: Partial<LineItem>) => void
}

export const LineItemImage: React.FC<Props> = ({ item, onUpdate }) => {
  const [loading, setLoading] = useState(false)
  const setImageEditorEntity = useFFEStore(
    (state) => state.setImageEditorEntity
  )
  const setLineItemDetailsDrawerId = useFFEStore(
    (state) => state.setLineItemDetailsDrawerId
  )
  const api = useApi()

  const handleUpload: UploadProps['customRequest'] = async ({
    file,
    onSuccess
  }) => {
    try {
      setLoading(true)

      if (!(file instanceof File)) {
        throw new Error('Invalid file')
      }

      // Create object URL for preview
      const objectUrl = await api.uploadImage(file)

      onUpdate({
        image: {
          url: objectUrl,
          alt: file.name
        }
      })

      onSuccess?.(null)
    } catch {
      message.error('Failed to upload image')
    } finally {
      setLoading(false)
    }
  }

  const handleSwapImage = (selected: NonNullable<LineItem['image']>) => {
    const currentMain = item.image
    const additional = item.additionalImages ?? []
    const filteredAdditional = additional.filter((img) => img.url !== selected.url)
    const newAdditional = currentMain ? [currentMain, ...filteredAdditional] : filteredAdditional

    onUpdate({
      image: selected,
      additionalImages: newAdditional
    })
  }

  return (
    <>
      <div className="relative">
        <Spin style={{ width: 70, height: 70 }} spinning={loading}>
          {(item.additionalImages?.length ?? 0) > 0 ? (
            <Popover
              placement="bottomLeft"
              trigger={['hover']}
              mouseEnterDelay={0.1}
              content={
                <div
                  style={{ maxWidth: 320, maxHeight: 240, overflow: 'auto' }}
                  className="flex flex-wrap gap-2 p-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.additionalImages!.map((img) => (
                    <img
                      key={img.url}
                      src={img.url}
                      alt={img.alt}
                      style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSwapImage(img)
                      }}
                    />)
                  )}
                </div>
              }
            >
              <Upload
                accept="image/*"
                showUploadList={false}
                customRequest={handleUpload}
                className="w-[70px] h-[70px] flex-shrink-0"
              >
                {item.image ? (
                  <img
                    style={{ width: 70, height: 70, objectFit: 'contain' }}
                    src={item.image.url}
                    alt={item.image.alt}
                    className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation()
                      setImageEditorEntity({
                        type: 'lineItem',
                        id: item.id
                      })
                    }}
                  />
                ) : (
                  <div
                    style={{ width: 70, height: 70 }}
                    className="w-full h-full border border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-primary transition-colors"
                  >
                    <ImagePlus className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </Upload>
            </Popover>
          ) : (
            <Upload
              accept="image/*"
              showUploadList={false}
              customRequest={handleUpload}
              className="w-[70px] h-[70px] flex-shrink-0"
            >
              {item.image ? (
                <img
                  style={{ width: 70, height: 70, objectFit: 'contain' }}
                  src={item.image.url}
                  alt={item.image.alt}
                  className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation()
                    setImageEditorEntity({
                      type: 'lineItem',
                      id: item.id
                    })
                  }}
                />
              ) : (
                <div
                  style={{ width: 70, height: 70 }}
                  className="w-full h-full border border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-primary transition-colors"
                >
                  <ImagePlus className="h-5 w-5 text-gray-400" />
                </div>
              )}
            </Upload>
          )}
        </Spin>
        {item.image && (
          <Button
            type="text"
            size="small"
            icon={<Maximize2 className="h-3 w-3" />}
            className="absolute -top-2 -right-2 w-6 h-6 p-0 bg-white shadow-md rounded-full flex items-center justify-center hover:bg-gray-50"
            onClick={(e) => {
              e.stopPropagation()
              setImageEditorEntity({
                type: 'lineItem',
                id: item.id
              })
              setLineItemDetailsDrawerId(undefined)
            }}
          />
        )}
      </div>
    </>
  )
}
